import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  CssBaseline,
  Backdrop,
  CircularProgress,
  Avatar,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Tab,
  Tabs,
  Chip,
  useTheme,
  Alert,
  AlertTitle,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import WorkIcon from "@mui/icons-material/Work";
import CampaignIcon from "@mui/icons-material/Campaign";
import HandshakeIcon from "@mui/icons-material/Handshake";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ChatIcon from "@mui/icons-material/Chat";
import LockIcon from "@mui/icons-material/Lock";

import API from "../../../API";
import ChatModal from "./chatmodal";
import PageTitle from "../../../Components/PageTitle";
import useAlertDialog from "../../../Components/useAlertDialog";
import AlertDialog from "../../../Components/AlertDialog";
import useAuth from "../../../Hooks/use-auth";
import HomeHeader from "../../../Components/HomeHeader";
/* ────────────────────────────────────────────────────────────────
  Helpers & Styled bits
──────────────────────────────────────────────────────────────── */
const iconForProject = (type) => {
  switch (type) {
    case "campaign":
      return <CampaignIcon />;
    case "partnership":
      return <HandshakeIcon />;
    case "payout":
      return <AttachMoneyIcon />;
    default:
      return <CampaignIcon />;
  }
};

// Dark mode friendly gradients
const lightGradient = "linear-gradient(45deg, #646AFF 0%, #7B2A2A 100%)";
const darkGradient = "linear-gradient(45deg, #4A5BF0 0%, #6B1A1A 100%)";

// Color scheme for different project types (dark mode compatible)
const getProjectColors = (type, isDark) => {
  const colors = {
    campaign: {
      light: { border: "#646AFF", bg: "#F3F4FF", text: "#2C2C54" },
      dark: { border: "#5A6CFF", bg: "#1A1A2E", text: "#E0E6FF" }
    },
    partnership: {
      light: { border: "#4CAF50", bg: "#F1F8E9", text: "#1B5E20" },
      dark: { border: "#66BB6A", bg: "#0D2818", text: "#C8E6C9" }
    },
    payout: {
      light: { border: "#FF9800", bg: "#FFF8E1", text: "#E65100" },
      dark: { border: "#FFB74D", bg: "#2C1810", text: "#FFE0B2" }
    }
  };
  
  return colors[type]?.[isDark ? 'dark' : 'light'] || colors.campaign[isDark ? 'dark' : 'light'];
};

/* ────────────────────────────────────────────────────────────────
  Component
──────────────────────────────────────────────────────────────── */
export default function UserProfile() {
  const { userId } = useParams();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { dialogState, openDialog } = useAlertDialog();
  const { isAuthorized, getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser?.();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);

  /* ───── fetch ───── */
  useEffect(() => {
    let mounted = true;
    const fetchUser = async () => {
      try {
        const res = await API.users.fetchPublicDetails(userId);
        if (mounted) setUser(res);
      } catch (err) {
        openDialog("Error", err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchUser();
    return () => (mounted = false);
  }, [userId, openDialog]);

  /* ───── derived ───── */
  const {
    first_name: first = "",
    last_name: last = "",
    company = {},
    campaigns = [],
    partnerships = [],
    payouts = [],
    created_at,
  } = user || {};

  const activePartnerships = useMemo(
    () => partnerships.filter((p) => p.status?.toUpperCase() !== "DELETED"),
    [partnerships]
  );

  const projects = useMemo(
    () => [...campaigns, ...activePartnerships, ...payouts],
    [campaigns, activePartnerships, payouts]
  );

  /* ───── chat handlers ───── */
  const handleChatClick = () => {
    if (!isAuthorized) {
      openDialog(
        "Authentication Required", 
        "You must be logged in to start a chat conversation. Please log in to your account first."
      );
      return;
    }
    setChatOpen(true);
  };

  /* ───── ui state ───── */
  if (loading)
    return (
      <Backdrop open sx={{ color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );

  if (!user)
    return (
      <Box p={4} textAlign="center">
        <Typography color={isDarkMode ? "#E0E0E0" : "#333333"}>
          No user data found.
        </Typography>
      </Box>
    );

  /* ───── render helpers ───── */
  const InfoChip = ({ icon, label, value }) => (
    <Chip
      icon={icon}
      label={
        <Typography 
          component="span" 
          fontWeight={600} 
          sx={{ 
            mr: 0.5,
            color: isDarkMode ? "#FFFFFF" : "#1A1A1A"
          }}
        >
          {value || "N/A"}
        </Typography>
      }
      sx={{ 
        mr: 1, 
        mb: 1, 
        backgroundColor: isDarkMode ? "#2C2C2C" : "#F5F5F5",
        color: isDarkMode ? "#FFFFFF" : "#333333",
        border: `1px solid ${isDarkMode ? "#404040" : "#E0E0E0"}`,
        '& .MuiChip-icon': {
          color: isDarkMode ? "#B0B0B0" : "#666666"
        }
      }}
    />
  );

  const ProjectCard = ({ project }) => {
    const projectColors = getProjectColors(project.type, isDarkMode);
    
    return (
      <Paper
        elevation={isDarkMode ? 8 : 1}
        sx={{
          p: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          borderLeft: `5px solid ${projectColors.border}`,
          backgroundColor: projectColors.bg,
          transition: "all 0.25s ease",
          "&:hover": { 
            transform: "translateY(-4px)", 
            boxShadow: isDarkMode ? 12 : 4,
            backgroundColor: isDarkMode ? "#252525" : "#FAFAFA"
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Box sx={{ color: projectColors.border }}>
            {iconForProject(project.type)}
          </Box>
          <Typography 
            variant="subtitle1" 
            fontWeight={700} 
            noWrap
            sx={{ color: projectColors.text }}
          >
            {project.name || `Payout #${project.id}`}
          </Typography>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={0.5}>
          <Chip 
            size="small" 
            label={project.status}
            sx={{
              backgroundColor: isDarkMode ? "#3C3C3C" : "#EEEEEE",
              color: isDarkMode ? "#FFFFFF" : "#333333",
              fontWeight: 600
            }}
          />
          {project.campaign_type && (
            <Chip 
              size="small" 
              label={project.campaign_type}
              sx={{
                backgroundColor: projectColors.border,
                color: "#FFFFFF",
                fontWeight: 600
              }}
            />
          )}
        </Box>
      </Paper>
    );
  };

  /* ───── view ───── */
  return (
    <>
      <CssBaseline />
      <PageTitle title={`${first} ${last} – Profile`} />
      <AlertDialog alertState={dialogState} />
        <HomeHeader />

      {/* Cover */}
      <Box
        sx={{
          height: 280,
          background: isDarkMode ? darkGradient : lightGradient,
          position: "relative",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          pb: 8,
        }}
      >
        <Avatar
          src={user.phphref || "/default-avatar.png"}
          sx={{
            width: 160,
            height: 160,
            border: `4px solid ${isDarkMode ? "#1E1E1E" : "#FFFFFF"}`,
            boxShadow: isDarkMode ? "0 8px 32px rgba(0,0,0,0.5)" : 4,
            position: "absolute",
            bottom: -80,
          }}
        />
      </Box>

      {/* Body */}
      <Box maxWidth="md" mx="auto" px={2}>
        {/* header */}
        <Box textAlign="center" mt={10} mb={4}>
          <Typography 
            variant="h4" 
            fontWeight={700} 
            gutterBottom
            sx={{ color: isDarkMode ? "#FFFFFF" : "#1A1A1A" }}
          >
            {first} {last}
          </Typography>
          
          <Box display="flex" justifyContent="center" flexWrap="wrap">
            <InfoChip icon={<PlaceIcon />} label="Location" value={company.location || "Global"} />
            {company.industry && (
              <InfoChip icon={<WorkIcon />} label="Industry" value={company.industry} />
            )}
            {created_at && (
              <InfoChip
                icon={<WorkIcon />}
                label="Member Since"
                value={new Date(created_at).toLocaleDateString()}
              />
            )}
          </Box>

          {/* Authentication Alert */}
          {!isAuthorized && (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 2, 
                mb: 2,
                backgroundColor: isDarkMode ? "#1A237E" : "#E3F2FD",
                color: isDarkMode ? "#BBDEFB" : "#0D47A1",
                "& .MuiAlert-icon": {
                  color: isDarkMode ? "#64B5F6" : "#1976D2"
                }
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>Login Required for Chat</AlertTitle>
              You must be logged in to start a conversation with this user.
            </Alert>
          )}

          {/* Chat Button */}
          <Button
            variant="contained"
            size="large"
            startIcon={isAuthorized ? <ChatIcon /> : <LockIcon />}
            sx={{ 
              mt: 3, 
              borderRadius: 8, 
              background: isAuthorized 
                ? (isDarkMode ? darkGradient : lightGradient)
                : (isDarkMode ? "#424242" : "#BDBDBD"),
              color: "#FFFFFF",
              fontWeight: 700,
              px: 4,
              py: 1.5,
              boxShadow: isAuthorized ? 4 : 1,
              "&:hover": {
                background: isAuthorized 
                  ? (isDarkMode ? "linear-gradient(45deg, #3A4BE0 0%, #5B0A0A 100%)" : "linear-gradient(45deg, #5A6CFF 0%, #6B1A1A 100%)")
                  : (isDarkMode ? "#616161" : "#9E9E9E"),
                boxShadow: isAuthorized ? 6 : 1,
              },
              "&:disabled": {
                background: isDarkMode ? "#424242" : "#BDBDBD",
                color: isDarkMode ? "#757575" : "#FFFFFF"
              }
            }}
            onClick={handleChatClick}
            disabled={!isAuthorized}
          >
            {isAuthorized 
              ? `Chat with ${first.split(" ")[0] || "user"}`
              : "Login to Chat"
            }
          </Button>
        </Box>

        {/* Tabs */}
        <Paper 
          elevation={isDarkMode ? 8 : 1} 
          sx={{ 
            borderRadius: 4,
            backgroundColor: isDarkMode ? "#1E1E1E" : "#FFFFFF"
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            textColor="inherit"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                color: isDarkMode ? "#B0B0B0" : "#666666",
                fontWeight: 600,
                fontSize: "1rem"
              },
              "& .Mui-selected": {
                color: isDarkMode ? "#FFFFFF" : "#1A1A1A",
                fontWeight: 700
              }
            }}
          >
            <Tab label="Projects" />
            <Tab label="About" />
          </Tabs>
          <Divider sx={{ borderColor: isDarkMode ? "#333333" : "#E0E0E0" }} />

          {tab === 0 && (
            <Box p={3}>
              {projects.length ? (
                <Grid container spacing={2}>
                  {projects.map((p) => (
                    <Grid key={`${p.type}-${p.id}`} item xs={12} sm={6} md={4}>
                      <ProjectCard project={p} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography 
                  textAlign="center"
                  sx={{ 
                    color: isDarkMode ? "#B0B0B0" : "#666666",
                    fontStyle: "italic",
                    py: 4
                  }}
                >
                  No projects available.
                </Typography>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box p={3}>
              <Typography 
                variant="subtitle1" 
                gutterBottom
                sx={{ 
                  color: isDarkMode ? "#FFFFFF" : "#1A1A1A",
                  fontWeight: 700,
                  mb: 2
                }}
              >
                Bio
              </Typography>
              <Typography
                sx={{ 
                  color: isDarkMode ? "#E0E0E0" : "#333333",
                  lineHeight: 1.6,
                  fontSize: "1rem"
                }}
              >
                {user.bio || (
                  <em style={{ color: isDarkMode ? "#B0B0B0" : "#666666" }}>
                    This user hasn't written a bio yet.
                  </em>
                )}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Chat Modal - only render if authorized */}
      {isAuthorized && (
        <ChatModal 
          open={chatOpen} 
          onClose={() => setChatOpen(false)} 
          recipientId={userId} 
        />
      )}
    </>
  );
}